import { FC, useMemo } from 'react';

import { Image } from '../../atoms';
import { messageCardProps, messageProps } from './MessageCard.types';
import { StyledSection, StyledIcon, ImageContainer } from './MessageCard.styles';
import { CollapsibleMessages } from './CollapsibleMessages';

import { createImageSizes } from '../../utils';
import { URL_CONSTANTS, constants } from '../../constants';
import { getWindowSession, transformResponse, checkValidProductDiffContent, canUseDOM } from '../../utils';
import { SessionDataMap } from './MessageCard.schema';
import { usePageContext } from '../../organisms/PageContext';

const iconNameMap: Record<string, string> = {
  error: 'icon-warning',
  ocjError: 'icon-warning',
  info: 'icon-information',
  success: 'icon-success',
  alert: 'icon-alert',
  prefError: 'icon-warning',
  successMessage: 'icon-success',
  accessibilityAlerts: 'icon-accessibility',
  successMsg: 'icon-success',
  cancelSuccessMsg: 'icon-success',
  redemptionError: 'icon-alert',
  isAdultOnlyError: 'icon-alert',
  guestCountError: 'icon-alert',
  priceUpdateAlert: 'icon-warning',
  roomUpdateAlert: 'icon-warning',
  upcomingResAlert: 'icon-alert',
  fnaMessageBuyPoints: 'icon-alert',
  redemptionErrorMsg: 'icon-warning',
  restrictModification: 'icon-warning',
  creditCardAlert: 'icon-alert',
  creditCardAlertRRD: 'icon-alert',
  warning: 'icon-warning',
  availabilityError: 'icon-warning',
  cancelOTP: 'icon-warning',
  prearrivalVerification: 'icon-warning',
  foreignCurrencyAlert: 'icon-warning',
  currencyConversionAlert: 'icon-warning',
  prepayRateAlert: 'icon-information',
  acrsMemberBookingAlert: 'icon-warning',
  qrvVerificationSuccess: 'icon-success',
  priceChangeAlert: 'icon-information',
  unAvailableRoomAlert: 'icon-warning',
  fnaMessage: 'icon-elite-status',
  redemptionPanelError: 'icon-warning',
  phoneNumberSPSuccess: 'icon-success',
};
const classNameMap: Record<string, string> = {
  error: 'error',
  redemptionPanelError: 'redemption-panel-error',
  fnaMessage: 'fna-message',
  ocjError: 'ocj-error',
  info: 'information',
  success: 'success',
  alert: 'alert',
  alertError: 'alert-error',
  hotelmessage: 'hotel-message',
  hotelmessagemgp: 'hotel-message-mgp',
  prefError: 'pref-error',
  successMessage: 'success-message',
  accessibilityAlerts: 'accessibility-alert',
  graphQlError: 'alert-error mt-5 mb-0',
  successMsg: 'success-msg',
  cancelSuccessMsg: 'cancel-success-msg',
  failureMessage: 'failure-message',
  redemptionError: 'no-redemption-error',
  isAdultOnlyError: 'is-adult-error',
  guestCountError: 'is-adult-error',
  priceUpdateAlert: 'warning-msg',
  roomUpdateAlert: 'warning-msg',
  upcomingResAlert: 'alert upcomingres-alert',
  fnaMessageBuyPoints: 'fna-message-alert',
  redemptionErrorMsg: 'redemption-error-msg',
  restrictModification: 'modify-restrict',
  creditCardAlert: 'alert credit-card-alert mt-4 mb-5',
  creditCardAlertRRD: 'alert credit-card-alert mb-5',
  warning: 'warning-msg',
  availabilityError: 'warning-msg',
  cancelOTP: 'error cancel-otp',
  prearrivalVerification: 'prearrival-verification',
  foreignCurrencyAlert: 'foreign-currency-alert',
  currencyConversionAlert: 'foreign-currency-alert',
  prepayRateAlert: 'alert prepay-rate-alert',
  acrsMemberBookingAlert: 'acrs-member-booking-alert',
  qrvVerificationSuccess: 'success-message',
  priceChangeAlert: 'mb-3 mt-3 price-change-alert',
  unAvailableRoomAlert: 'mb-1 mt-3 p-3 error unavailable-room-alert',
  phoneNumberSPSuccess: 'success-message',
};

export const MessageCard: FC<messageCardProps> = ({
  type,
  messages,
  showCard,
  heading,
  fileReferenceDynamic,
  hideIcon,
  isLinkRequired = false,
  linkTitle = '',
  showModal = false,
  ariaLabel,
  setOpenModal,
  ariaLive,
  role,
  pageType,
  collapsible = false,
  moreLabel,
  lessLabel,
  maxCollapsedLines = 3,
}: messageCardProps) => {
  const iconName = iconNameMap[type] || 'icon-information';
  const { RLM_PAGE, SONDAR_BRAND_CODE, ACCESSIBILITY_ALERTS, FOREIGN_CURRENCY_ALERT, CLASS_FOREIGN_CURRENCY_ALERT } =
    constants;

  const sessionObject = getWindowSession();
  const { brandCode } = transformResponse(sessionObject, SessionDataMap);

  const { pageState } = usePageContext();

  // Get product differentiation content from page state
  const productDifferentiationContent = useMemo(() => {
    return pageState?.rateCard?.productDifferentiationContent;
  }, [pageState?.rateCard?.productDifferentiationContent]);
  const { contentReference: productDiffSegId } = productDifferentiationContent || {};

  // check valid product differentiation content based on the segment ID
  const isProductDiffContentValid = checkValidProductDiffContent(productDiffSegId);

  const isSondar = pageType === RLM_PAGE && brandCode === SONDAR_BRAND_CODE;
  const className =
    (isSondar || isProductDiffContentValid) && type !== ACCESSIBILITY_ALERTS
      ? `${classNameMap[type]} col-lg-7 mt-lg-2`
      : classNameMap[type];

  //to remove mt-5 class if graphql error mssg is inside r0-checkout-wrapper-top
  const handleMessageCardRef = (element: HTMLElement | null) => {
    if (canUseDOM && element && type === 'graphQlError' && element.closest('.r0-checkout-wrapper-top')) {
      element.classList.remove('mt-5');
    }
  };
  const renditions = fileReferenceDynamic?.renditions;
  const imageSize = createImageSizes(renditions);

  // remove the space for Foreign Currency Alert when it does show up
  if (canUseDOM && isProductDiffContentValid) {
    const foreignCurrencyAlertElement = document.querySelector(`.${CLASS_FOREIGN_CURRENCY_ALERT}`);

    if (foreignCurrencyAlertElement) {
      const messageCards = Array.from(document.querySelectorAll('[data-testid="book-MessageCard"]'));
      const hasForeignCurrencyAlert = messageCards.some(el => el.id === FOREIGN_CURRENCY_ALERT);
      if (!hasForeignCurrencyAlert) {
        // remove the d-block class from foreign currency container when it is not present in the messages to avoid extra space
        foreignCurrencyAlertElement?.classList.remove('d-block');
      }
    }
  }

  return showCard ? (
    <StyledSection
      ref={handleMessageCardRef}
      data-component-name="m-book-MessageCard"
      data-testid="book-MessageCard"
      id={type}
      className={className}
      {...(ariaLive && { 'aria-live': ariaLive })}
      {...(role && { role: role })}
    >
      {fileReferenceDynamic?.damPath && (
        <ImageContainer>
          <Image
            url={fileReferenceDynamic?.damPath}
            renditions={fileReferenceDynamic?.renditions}
            dynamic={fileReferenceDynamic?.dynamic}
            size={imageSize}
            appliedClass={'image-view'}
          />
        </ImageContainer>
      )}
      {!hideIcon && <StyledIcon className={[iconName, 'hide-icon'].join(' ')} />}
      <span>
        {heading && <span className="messagecard-heading">{heading}</span>}
        <CollapsibleMessages
          collapsible={collapsible}
          moreLabel={moreLabel}
          lessLabel={lessLabel}
          maxCollapsedLines={maxCollapsedLines}
        >
          {messages.map((item: messageProps, index) => {
            const keyBase = `${index}-messageCard`;
            // RETURN COMPLETE COMPONENT AS A MESSAGE
            if ('component' in item && item.component) {
              return (
                <div className="message" key={`${keyBase}-component`}>
                  {item.component}
                </div>
              );
            }
            // RETURN RICH/TREATED-AS-HTML TEXT MESSAGES (error, ocjError, or isRichText)
            const { text, isRichText } = item as Extract<messageProps, { text: string }>;
            if (type === 'error' || type === 'ocjError' || isRichText || type === 'redemptionPanelError') {
              return (
                <div
                  key={index.toString() + 'messageCardRichText'}
                  data-rte-editelement
                  className="message"
                  dangerouslySetInnerHTML={{ __html: text }}
                />
              );
            }
            //RETURN PLAIN TEXT MESSAGES (with optional link)
            return (
              <p className="message" key={index.toString() + 'messageCard'}>
                {text}
                {isLinkRequired &&
                  (showModal ? (
                    setOpenModal && (
                      <a href="#" className="linktitle ml-1 t-font-weight-r" onClick={() => setOpenModal(true)}>
                        {linkTitle}
                      </a>
                    )
                  ) : (
                    <a
                      href={URL_CONSTANTS.TI_REDIRECT_STATIC_LINK}
                      className="standard d-inline-block"
                      target="_blank"
                      rel="noreferrer"
                      aria-label={ariaLabel?.replace('{XX}', linkTitle)}
                    >
                      <span className="linktitle">{linkTitle}</span>
                      <span className="icon-external-arrow"></span>
                    </a>
                  ))}
              </p>
            );
          })}
        </CollapsibleMessages>
      </span>
    </StyledSection>
  ) : null;
};
